import type {
  AssignmentKind,
  ClassAssignmentsResponse,
  ClassDetail,
  CustomExerciseDto,
  ExerciseSummary,
  ProjectSummary,
} from '@pebblecode/shared';
import { useCallback, useEffect, useState, type FormEvent } from 'react';

import { api } from '../api/client.js';
import { onLinkClick } from '../app/router.js';
import { Icon } from '../components/Icon.js';
import { Field, FormError } from '../components/PageShell.js';
import { useAction } from '../components/useAction.js';
import { useT } from '../i18n/index.js';
import { useUiLang } from '../state/prefs.js';
import { useDueText } from './dueText.js';
import { HomeworkList } from './HomeworkList.js';

interface Choices {
  exercise: ExerciseSummary[];
  custom: CustomExerciseDto[];
  project: ProjectSummary[];
}

/** The teacher's "Assign homework" form. */
function AssignForm({ classId, onDone }: { classId: string; onDone: () => void }) {
  const t = useT();
  const lang = useUiLang();
  const [kind, setKind] = useState<AssignmentKind>('exercise');
  const [choices, setChoices] = useState<Choices | null>(null);
  const [item, setItem] = useState('');
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [due, setDue] = useState('');
  const act = useAction();

  useEffect(() => {
    let live = true;
    void Promise.all([api.exercises(lang), api.myExercises().catch(() => []), api.projects()])
      .then(([ex, custom, projects]) => {
        if (!live) return;
        setChoices({
          exercise: ex.exercises,
          custom,
          project: projects.filter((p) => !p.exerciseSlug),
        });
      })
      .catch(() => live && setChoices({ exercise: [], custom: [], project: [] }));
    return () => {
      live = false;
    };
  }, [lang]);

  const options =
    choices === null
      ? []
      : kind === 'exercise'
        ? choices.exercise.map((e) => ({
            id: e.slug,
            label: `${t.category[e.category]} · ${e.title}`,
          }))
        : kind === 'custom'
          ? choices.custom.map((e) => ({ id: e.slug, label: e.title }))
          : choices.project.map((p) => ({ id: p.id, label: p.title }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!item) return;
    void act.run(
      async () => {
        await api.createAssignment(classId, {
          ...(kind === 'project' ? { projectId: item } : { exerciseSlug: item }),
          ...(title.trim() ? { title: title.trim() } : {}),
          note: note.trim(),
          dueDate: due || null,
        });
        onDone();
      },
      () => t.common.somethingWrong,
    );
  };

  return (
    <form className="form-card card" onSubmit={submit}>
      <h2>{t.homework.assign}</h2>
      <Field label={t.homework.assign}>
        <select
          value={kind}
          onChange={(e) => {
            setKind(e.target.value as AssignmentKind);
            setItem('');
          }}
        >
          {(['exercise', 'custom', 'project'] as const).map((k) => (
            <option key={k} value={k}>
              {t.homework.kind[k]}
            </option>
          ))}
        </select>
      </Field>
      <Field label={t.homework.kind[kind]}>
        <select value={item} onChange={(e) => setItem(e.target.value)} required>
          <option value="">{choices ? t.homework.pick : t.common.loading}</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
      </Field>
      <Field label={t.homework.itemTitle}>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={80}
          placeholder={
            options
              .find((o) => o.id === item)
              ?.label.split(' · ')
              .at(-1) ?? ''
          }
        />
      </Field>
      <Field label={t.homework.note}>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={1000}
          rows={2}
        />
      </Field>
      <Field label={t.homework.dueDate}>
        <input type="date" value={due} onChange={(e) => setDue(e.target.value)} />
      </Field>
      <FormError text={act.error} />
      <div className="button-row">
        <button className="btn btn-primary" type="submit" disabled={act.busy || !item}>
          {t.homework.create}
        </button>
      </div>
    </form>
  );
}

/** Homework on a class page: the teacher's list with progress, or the student's own list. */
export function ClassHomework({ classDetail }: { classDetail: ClassDetail }) {
  const t = useT();
  const dueText = useDueText();
  const [data, setData] = useState<ClassAssignmentsResponse | null>(null);
  const [adding, setAdding] = useState(false);
  const [confirm, setConfirm] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .classAssignments(classDetail.id)
      .then(setData)
      .catch(() => setData({ teacher: [], student: null }));
  }, [classDetail.id]);
  useEffect(load, [load]);

  if (!data) return <p className="muted-text">{t.common.loading}</p>;

  if (data.student) {
    return (
      <section className="card form-card">
        <h2>{t.homework.title}</h2>
        <HomeworkList items={data.student} onChange={load} />
      </section>
    );
  }

  const list = data.teacher ?? [];
  return (
    <section className="page-column" style={{ gap: 16, margin: 0, maxWidth: 'none' }}>
      <div className="page-head">
        <h2 className="page-title" style={{ fontSize: 20 }}>
          {t.homework.title}
        </h2>
        <div className="topbar-spacer" />
        <button className="btn btn-primary btn-sm" onClick={() => setAdding((a) => !a)}>
          <Icon name={adding ? 'close' : 'plus'} size={13} strokeWidth={2.6} />
          {adding ? t.common.cancel : t.homework.assign}
        </button>
      </div>
      {adding && (
        <AssignForm
          classId={classDetail.id}
          onDone={() => {
            setAdding(false);
            load();
          }}
        />
      )}
      {list.length === 0 && !adding && <p className="form-note">{t.homework.noneTeacher}</p>}
      <ul className="hw-list">
        {list.map((a) => (
          <li key={a.id} className="hw-item">
            <div className="hw-item-top">
              <strong>{a.title}</strong>
              <span className="tag">{t.homework.kind[a.kind]}</span>
            </div>
            <span className="hw-meta">
              {dueText(a.dueDate)} · {t.homework.progress(a.done, a.students)} ·{' '}
              {t.homework.startedCount(a.started)}
            </span>
            <div className="progress-bar" aria-hidden="true">
              <i style={{ width: `${a.students ? (100 * a.done) / a.students : 0}%` }} />
            </div>
            {a.note && <p className="hw-note">{a.note}</p>}
            <div className="button-row">
              <a href={`/assignments/${a.id}`} onClick={onLinkClick} className="btn btn-sm">
                <Icon name="eye" size={13} />
                {t.homework.seeWork}
              </a>
              <button
                className={`btn btn-sm${confirm === a.id ? ' btn-danger' : ''}`}
                onClick={() => {
                  if (confirm !== a.id) {
                    setConfirm(a.id);
                    return;
                  }
                  setConfirm(null);
                  void api.deleteAssignment(a.id).then(load);
                }}
              >
                {confirm === a.id ? t.homework.deleteConfirm(a.title) : t.homework.delete}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
